import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';
import { SearchBar } from './SearchBar';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
  { code: 'de', label: 'DE' }
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const navigationStructure = [
    {
      name: t('nav.company'),
      hasDropdown: true,
      mainPath: '/',
      items: [
        { name: t('nav.home'), path: '/' },
        { name: t('nav.about'), path: '/about' },
        { name: t('nav.ceoMessage'), path: '/ceo-message' },
        { name: t('nav.orgChart'), path: '/organisation-chart' },
        { name: t('nav.events'), path: '/company/events' },
        { name: t('nav.howWeWork'), path: '/how-we-work' },
        { name: t('nav.careers'), path: '/careers' },
        { name: t('nav.contactUs'), path: '/get-in-touch' }
      ]
    },
    {
      name: t('nav.services'),
      hasDropdown: true,
      mainPath: '/services',
      items: [
        { name: t('nav.integratedMaterials'), path: '/services/integrated' },
        { name: t('nav.specialtyProduction'), path: '/services/production' }
      ]
    },
    {
      name: t('nav.industries'),
      hasDropdown: true,
      mainPath: '/industries',
      items: [
        { name: t('nav.display'), path: '/industries/display' },
        { name: t('nav.semiconductor'), path: '/industries/semiconductor' },
        { name: t('nav.personalCare'), path: '/industries/personal-care' }
      ]
    },
    {
      name: t('nav.capabilities'),
      hasDropdown: true,
      mainPath: '/capabilities',
      items: [
        { name: t('nav.rd'), path: '/capabilities/rd' },
        { name: t('nav.purification'), path: '/capabilities/purifications' },
        { name: t('nav.facility'), path: '/capabilities/facility' },
        { name: t('nav.productionPlants'), path: '/capabilities/production-plants' },
        { name: 'Quality & Compliance', path: '/quality' },
        { name: 'Products & Capabilities', path: '/products' }
      ]
    },
    { name: 'Research', path: '/research-highlights/index.html', hasDropdown: false, isStatic: true },
    {
      name: t('nav.platformBrands'),
      hasDropdown: true,
      mainPath: '/brands',
      items: [
        { name: 'LUMORA', path: 'https://lumorachemicals.com/', external: true },
        { name: 'Research Highlights', path: '/research-highlights/index.html', external: true }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  const handleDropdownToggle = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img 
              src="/lamko-logo.png" 
              alt="LAMKO" 
              className="h-11 md:h-13 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationStructure.map((item) => (
              item.hasDropdown ? (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.name)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <div className="flex items-center">
                    <Link
                      to={item.mainPath || '#'}
                      className="px-3 py-2 rounded-l-md text-sm font-medium text-gray-200 hover:text-orange-400 hover:bg-gray-800 transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                    <button
                      className="px-1 py-2 rounded-r-md text-sm font-medium text-gray-200 hover:text-orange-400 hover:bg-gray-800 transition-colors duration-200"
                    >
                      <ChevronDown size={16} className={`transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  {openDropdown === item.name && (
                    <div 
                      className="absolute top-full left-0 mt-0 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                      onMouseEnter={() => setOpenDropdown(item.name)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.items.map((subItem) => (
                        subItem.external ? (
                          <a
                            key={subItem.path}
                            href={subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2 text-sm text-gray-700 hover:text-orange-600 hover:bg-gray-50 transition-colors duration-200"
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`block px-4 py-2 text-sm ${
                              isActive(subItem.path)
                                ? 'text-orange-600 bg-orange-50'
                                : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                            } transition-colors duration-200`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {subItem.name}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : item.isStatic ? (
                <a key={item.name} href={item.path} className="px-4 py-2 rounded-md text-sm font-medium text-gray-200 hover:text-orange-400 hover:bg-gray-800 transition-colors duration-200">{item.name}</a>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-orange-400 bg-gray-800'
                      : 'text-gray-200 hover:text-orange-400 hover:bg-gray-800'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* Language Switcher + CTA + Search */}
          <div className="hidden lg:flex items-center space-x-2">
            {/* Language Switcher */}
            <div
              className="relative"
              onMouseEnter={() => setLangOpen(true)}
              onMouseLeave={() => setLangOpen(false)}
            >
              <button
                className="flex items-center space-x-1.5 px-3 py-2 rounded-md text-sm font-medium text-gray-200 hover:text-orange-400 hover:bg-gray-800 transition-colors duration-200"
                data-testid="language-switcher"
              >
                <Globe size={16} />
                <span>{currentLang.label}</span>
                <ChevronDown size={14} className={`transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-0 w-32 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      data-testid={`lang-${lang.code}`}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${
                        i18n.language === lang.code
                          ? 'text-orange-600 bg-orange-50 font-semibold'
                          : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            
            <Link to="/lumi" className="hidden lg:inline-flex items-center px-3 py-1.5 rounded-md text-white text-sm font-semibold whitespace-nowrap mr-2" style={{ background: "linear-gradient(90deg,#EA580C,#F59E0B)" }}>LUMI</Link>
            <Link to="/partner" data-testid="nav-partner-cta">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white whitespace-nowrap">
                {t('nav.partnerWithUs')}
              </Button>
            </Link>

            {/* Search Icon - expands right */}
            <SearchBar />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-200 hover:bg-gray-800"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-gray-900 border-t border-gray-800">
          <nav className="px-4 pt-2 pb-4 space-y-1">
            {/* Mobile Search */}
            <SearchBar isMobile onNavigate={() => { setIsMenuOpen(false); setOpenDropdown(null); }} />

            {/* Mobile Language Switcher */}
            <div className="flex items-center space-x-2 px-4 py-3 border-b border-gray-800 mb-2">
              <Globe size={16} className="text-gray-400" />
              <div className="flex space-x-1">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                      i18n.language === lang.code
                        ? 'bg-orange-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {navigationStructure.map((item) => (
              item.hasDropdown ? (
                <div key={item.name}>
                  <button
                    onClick={() => handleDropdownToggle(item.name)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-md text-base font-medium text-gray-200 hover:text-orange-400 hover:bg-gray-800"
                  >
                    <span>{item.name}</span>
                    <ChevronDown size={16} className={`transition-transform duration-200 ${openDropdown === item.name ? 'rotate-180' : ''}`} />
                  </button>
                  {openDropdown === item.name && (
                    <div className="pl-4 space-y-1">
                      {item.items.map((subItem) => (
                        subItem.external ? (
                          <a
                            key={subItem.path}
                            href={subItem.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2 rounded-md text-sm text-gray-300 hover:text-orange-400 hover:bg-gray-800"
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => {
                              setIsMenuOpen(false);
                              setOpenDropdown(null);
                            }}
                            className={`block px-4 py-2 rounded-md text-sm ${
                              isActive(subItem.path)
                                ? 'text-orange-400 bg-gray-800'
                                : 'text-gray-300 hover:text-orange-400 hover:bg-gray-800'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : item.isStatic ? (
                <a key={item.name} href={item.path} className="block px-4 py-3 rounded-md text-base font-medium text-gray-200 hover:text-orange-400 hover:bg-gray-800">{item.name}</a>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-md text-base font-medium ${
                    isActive(item.path)
                      ? 'text-orange-400 bg-gray-800'
                      : 'text-gray-200 hover:text-orange-400 hover:bg-gray-800'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
            <Link to="/partner" onClick={() => setIsMenuOpen(false)} data-testid="mobile-nav-partner-cta">
              <Button className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white">
                {t('nav.partnerWithUs')}
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
